import { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { GameCanvas } from '@/game/GameCanvas';
import type { Runner } from '@/game/three/Runner';
import { gameBus, GameEvent, type GameStatus } from '@/game/events';
import { GameModal, GameModalButton } from '@/game/ui/GameModal';
import { BrandGuard } from '@/brand/BrandGuard';
import { BRAND } from '@/brand/brand';
import { readStored, writeStored } from '@/lib/native';
import { isMuted, setMuted, unlockAudio } from '@/game/audio';

const BEST_KEY = 'best-score';

/**
 * HUD and game-state UI around the canvas. Everything here is React and repaints rarely; the game
 * loop stays inside the engine.
 *
 * Every state that interrupts play goes through ONE component (`GameModal`) so the game's dialogs
 * are designed once and stay consistent — add a settings or level-complete state by rendering
 * another GameModal, never by hand-rolling another overlay.
 *
 * Colour, type and radius come from the brand; nothing here hard-codes an appearance.
 */
export function GameShell() {
	const runnerRef = useRef<Runner | null>(null);
	const [status, setStatus] = useState<GameStatus>('playing');
	const [score, setScore] = useState(0);
	const [best, setBest] = useState(0);
	const [muted, setMutedState] = useState(isMuted());
	const [pop, setPop] = useState<{ id: number; amount: number } | null>(null);
	const [showHint, setShowHint] = useState(true);

	useEffect(() => {
		readStored<number>(BEST_KEY).then((stored) => setBest(stored ?? 0));
	}, []);

	useEffect(() => {
		const onScore = (value: number) => setScore(value);
		const onStatus = (value: GameStatus) => setStatus(value);
		const onNearMiss = (amount: number) => setPop({ id: Date.now(), amount });
		gameBus.on(GameEvent.Score, onScore);
		gameBus.on(GameEvent.Status, onStatus);
		gameBus.on(GameEvent.NearMiss, onNearMiss);
		return () => {
			gameBus.off(GameEvent.Score, onScore);
			gameBus.off(GameEvent.Status, onStatus);
			gameBus.off(GameEvent.NearMiss, onNearMiss);
		};
	}, []);

	useEffect(() => {
		if (!pop) return;
		const t = setTimeout(() => setPop(null), 700);
		return () => clearTimeout(t);
	}, [pop]);

	// Persist the best score when a run ends, not every frame.
	useEffect(() => {
		if (status !== 'over' || score <= best) return;
		setBest(score);
		void writeStored(BEST_KEY, score);
	}, [status, score, best]);

	const dismissHint = useCallback(() => {
		setShowHint(false);
		unlockAudio();
	}, []);

	const restart = () => {
		runnerRef.current?.resume();
		runnerRef.current?.restart();
		setShowHint(false);
	};

	const togglePause = () => {
		if (status === 'paused') {
			runnerRef.current?.resume();
			setStatus('playing');
			return;
		}
		runnerRef.current?.pause();
		setStatus('paused');
	};

	const toggleMute = () => {
		const next = !muted;
		setMuted(next);
		setMutedState(next);
	};

	const isNewBest = status === 'over' && score >= best && score > 0;
	const controlStyle = { background: `${BRAND.color.onBase}1a` };

	return (
		<div
			className="relative h-dvh w-full overflow-hidden"
			style={{ background: BRAND.color.base, color: BRAND.color.onBase, fontFamily: BRAND.type.body }}
			onPointerDown={dismissHint}
		>
			<GameCanvas onReady={(runner) => (runnerRef.current = runner)} />

			{/* HUD. pointer-events-none so taps fall through to the game; controls opt back in. */}
			<div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-5 pt-[calc(1.25rem+env(safe-area-inset-top))]">
				<div>
					<div
						className="text-[44px] font-semibold leading-none tabular-nums drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
						style={{ fontFamily: BRAND.type.display }}
					>
						{score}
					</div>
					<div className="mt-1 text-[11px] font-medium uppercase tracking-[0.18em] opacity-60">
						Best {best}
					</div>
				</div>

				<div className="flex items-center gap-2">
					<button
						onClick={toggleMute}
						aria-label={muted ? 'Unmute' : 'Mute'}
						className="pointer-events-auto flex size-11 items-center justify-center rounded-full backdrop-blur transition-opacity hover:opacity-80"
						style={controlStyle}
					>
						{muted ? <VolumeX className="size-5" /> : <Volume2 className="size-5" />}
					</button>
					<button
						onClick={togglePause}
						aria-label={status === 'paused' ? 'Resume' : 'Pause'}
						className="pointer-events-auto flex size-11 items-center justify-center rounded-full backdrop-blur transition-opacity hover:opacity-80"
						style={controlStyle}
					>
						{status === 'paused' ? <Play className="size-5" /> : <Pause className="size-5" />}
					</button>
				</div>
			</div>

			{/* Near-miss reward. Feedback for nerve has to be seen, not just scored. */}
			{pop && (
				<div
					key={pop.id}
					className="pointer-events-none absolute left-1/2 top-[38%] -translate-x-1/2 text-2xl font-semibold"
					style={{ color: BRAND.color.accent, animation: 'popUp 700ms ease-out forwards' }}
				>
					+{pop.amount} close
				</div>
			)}

			{showHint && status === 'playing' && (
				<div className="pointer-events-none absolute inset-x-0 bottom-[12%] flex flex-col items-center gap-1 text-center">
					<p className="text-sm font-medium opacity-90">Swipe to switch lane · tap to jump</p>
					<p className="text-xs opacity-55">Skim a block for a bonus</p>
				</div>
			)}

			<GameModal
				open={status === 'over'}
				weight="full"
				eyebrow="Run ended"
				headline={score}
				sub={isNewBest ? <span style={{ color: BRAND.color.accent }}>New best</span> : `Best ${best}`}
			>
				<GameModalButton onClick={restart}>
					<RotateCcw className="size-4" />
					Play again
				</GameModalButton>
			</GameModal>

			<GameModal open={status === 'paused'} eyebrow="Paused" onDismiss={togglePause}>
				<GameModalButton onClick={togglePause}>
					<Play className="size-4" />
					Resume
				</GameModalButton>
			</GameModal>

			<BrandGuard />

			<style>{`
				@keyframes popUp {
					0%   { opacity: 0; transform: translate(-50%, 8px) scale(0.9); }
					25%  { opacity: 1; transform: translate(-50%, 0) scale(1); }
					100% { opacity: 0; transform: translate(-50%, -28px) scale(1); }
				}
				@media (prefers-reduced-motion: reduce) {
					@keyframes popUp { 0%, 100% { opacity: 1; transform: translate(-50%, 0); } }
				}
			`}</style>
		</div>
	);
}
